/**
 * The added notice: shows on the first add of a product, leaves by itself
 * after six seconds, and stays compact once the shopper has closed it.
 */
const VISIBLE_FOR = 6000;

class AddedNotice extends HTMLElement {
  #controller = new AbortController();
  #request;
  #timer;
  #quiet = false;

  connectedCallback() {
    const { signal } = this.#controller;

    document.addEventListener('cart:added', (event) => this.#onAdded(event.detail), { signal });
    this.addEventListener('click', (event) => event.target.closest('.sda-added__close') && this.#close(), { signal });
    this.addEventListener('pointerenter', () => clearTimeout(this.#timer), { signal });
    this.addEventListener('pointerleave', () => this.#arm(), { signal });
    this.addEventListener('focusin', () => clearTimeout(this.#timer), { signal });
    this.addEventListener('focusout', () => this.#arm(), { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#request?.abort();
    clearTimeout(this.#timer);
  }

  async #onAdded({ productId, productUrl, productTitle, source }) {
    if (!productId) return;
    const fromNotice = this.contains(source);
    const fresh = await this.#fetch(productId, productUrl);
    if (!fresh) return;

    if (fromNotice) {
      // An add from inside the notice only updates the shipping line.
      this.querySelector('.sda-added__ship')?.replaceWith(fresh.querySelector('.sda-added__ship'));
    } else if (this.hidden) {
      this.replaceChildren(...fresh.childNodes);
      this.querySelector('.sda-added__name').textContent = productTitle;
      this.classList.toggle('is-compact', this.#quiet || !this.querySelector('.sda-row'));
      this.hidden = false;
      this.classList.remove('is-in');
      void this.offsetWidth;
      this.classList.add('is-in');
    } else {
      // Already open: a new add only changes the name and the shipping line.
      this.querySelector('.sda-added__name').textContent = productTitle;
      this.querySelector('.sda-added__ship')?.replaceWith(fresh.querySelector('.sda-added__ship'));
    }
    this.#arm();
  }

  /**
   * Only products the shop linked by hand: the product's own combination
   * metafield, else the complementary products from Search & Discovery.
   */
  async #fetch(productId, productUrl) {
    this.#request?.abort();
    this.#request = new AbortController();
    const combined = new URL(productUrl, window.location.origin);
    combined.searchParams.set('section_id', 'added-notice');
    const complementary = `${this.dataset.url}?product_id=${productId}&intent=complementary&limit=6&section_id=added-notice`;

    try {
      const own = await this.#render(combined);
      if (own?.querySelector('.sda-row')) return own;
      return (await this.#render(complementary)) ?? own;
    } catch (error) {
      if (error.name === 'AbortError') return null;
      throw error;
    }
  }

  async #render(url) {
    const response = await fetch(url, { signal: this.#request.signal });
    if (!response.ok) return null;
    return new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('added-notice');
  }

  #arm() {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => (this.hidden = true), VISIBLE_FOR);
  }

  #close() {
    this.#quiet = true;
    clearTimeout(this.#timer);
    this.hidden = true;
  }
}

customElements.define('added-notice', AddedNotice);
